import type { Match } from '@uefadigital/panenka';
import {
  PkBadge,
  PkIdentifier,
  PkTable,
  PkTableBody,
  PkTableCell,
  PkTableHeader,
  PkTableHeaderCol,
  PkTableRow,
} from '@uefadigital/panenka-react';
import clsx from 'clsx';
import parse from 'html-react-parser';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useIsDesktop from 'hooks/useIsDesktop';
import type { Standing, TeamStanding } from 'types/ApiResponseInterfaces';
import { Qualified } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

interface StandingsData {
  teamStandings: Standing;
  competition: ConfigCompetition;
  match: Match;
}

const legendMap: Record<string, string> = {
  [Qualified.R16]: 'r16',
  [Qualified.R16_PO]: 'r16po',
  [Qualified.PO]: 'po',
  [Qualified.ELIMINATED]: 'eliminated',
} as const;

export const Standings = ({ teamStandings, competition, match }: StandingsData) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslation();
  const someQualified = useMemo(
    () => teamStandings.items.some((s) => s.qualified || !!s.qualificationLabelId),
    [teamStandings],
  );
  const seasonYear = parseInt(match.seasonYear, 10);
  const newTableSeason = competition?.newTableSeason ?? 0;
  const newFormat = newTableSeason !== 0 && newTableSeason <= seasonYear;

  const splitTablePlaces = newFormat && competition.splitTablePlaces;
  const splitConfig = newFormat && competition.splitConfig;
  const disableRank = teamStandings.items.every((item) => item.rank === 1);
  const labels = teamStandings.qualificationLabels ?? [];

  const mobileCriteria = ['played', 'won', 'drawn', 'lost', 'points'];
  const desktopCriteria = ['played', 'won', 'drawn', 'lost', 'goalsFor', 'goalsAgainst', 'goalDifference', 'points'];

  const criterias = isDesktop ? desktopCriteria : mobileCriteria;
  const teamsId = [match?.homeTeam?.id ?? '', match?.awayTeam?.id ?? ''];

  return (
    <div
      className={clsx(
        style.smTableFit,
        'pk-bg--elevation-01 pk-pt--m pk-pb--l pk-pb-md--m pk-py-md--xl pk-px--s pk-px-md--xl pk-mb--m pk-mb-md--xl',
        isDesktop && 'pk-rounded--xl pk-border',
      )}
    >
      <h3
        className={
          'pk-font-size--l pk-font-size-md--xl pk-line-height--sm pk-col pk-col--span-12 pk-text--text-01 pk-my--0 pk-my-md--auto pk-mx--0 pk-font--base-bold'
        }
      >
        {newFormat ? t('league_phase_table') : translateEntity(teamStandings.group)?.name}
      </h3>
      <PkTable
        key={`table_${isDesktop}`}
        rowSpacing={0}
        rowDivider={true}
        cellVerticalPad={isDesktop ? 12 : 0}
        cellHorizontalPad={isDesktop ? 8 : 0}
      >
        <PkTableHeader>
          <PkTableRow>
            {!disableRank && (
              <PkTableHeaderCol column-key="rank" aligned="center" is-hidden="false" canShrink={false} width={30} />
            )}
            <PkTableHeaderCol
              column-key="competitions"
              aligned="left"
              is-hidden="false"
              width={isDesktop ? 20 : 50}
              canGrow
              canShrink
              usePercentageWidth
            />
            {criterias.map((criteria, i) => {
              const isLastParameter = criterias.length - 1 === i;
              const criteriaSnakeCase = criteria
                .split(/(?=[A-Z])/)
                .join('_')
                .toLowerCase();

              return (
                <PkTableHeaderCol
                  columnKey={criteria}
                  key={criteria}
                  usePercentageWidth
                  aligned="center"
                  width={isLastParameter && !isDesktop ? 12 : 9}
                >
                  <span className={`${isLastParameter ? 'pk-font-weight--bolder' : ''} pk-font-size--xs`}>
                    {isDesktop ? t(criteriaSnakeCase) : t(`${criteriaSnakeCase}_small`)}
                  </span>
                </PkTableHeaderCol>
              );
            })}
          </PkTableRow>
        </PkTableHeader>

        <PkTableBody>
          {teamStandings.items?.map((teamData, i) => {
            return (
              <TableRow
                teamData={teamData}
                key={i}
                criterias={criterias}
                i={i}
                splitTablePlaces={splitTablePlaces}
                splitConfig={splitConfig}
                disableRank={disableRank}
                teamsId={teamsId}
              />
            );
          })}
        </PkTableBody>
      </PkTable>
      {newFormat && someQualified && (
        <div className="pk-d--flex pk-gap--s pk-pt--s pk-font-size--xs pk-text--text-02">
          {someQualified && (
            <div className={clsx(style.newFormatLegend, 'pk-d-sm--flex pk-gap--s pk-gap-sm--xl')}>
              {labels.map((l) => (
                <div
                  key={l.labelId}
                  className="pk-d--flex pk-align-items--center pk-justify-content--start pk-gap--xs2"
                >
                  <div className={clsx(style.legend, style[legendMap[l.labelId]])} />
                  {translateEntity(l).labelName}
                </div>
              ))}
            </div>
          )}

          <div
            className={clsx(
              style.newFormatLegend,
              !someQualified && style.newFormatLegendSingle,
              'pk-gap--s pk-d-md--none',
            )}
          >
            {mobileCriteria.map((item) => (
              <div key={item} className="pk-d--flex pk-align-items--start pk-justify-content--start pk-gap--xs2">
                <span className="pk-font--base-semibold pk-text--text-01">{t(`${item}_small`)}</span>
                {t(item)}
              </div>
            ))}
          </div>
        </div>
      )}
      {!newFormat && someQualified && (
        <div className="pk-d--flex pk-align-items--center pk-gap--xs pk-font-size--s pk-text--text-02">
          <div className={style.legend} />
          {t('qualified')}
        </div>
      )}
      {!!competition.procedureUrl && !!competition.procedureUrlText && newFormat && (
        <div className="pk-font-size--s pk-mt--m">
          {parse(t(competition.procedureUrlText).replace('{url}', competition.procedureUrl))}
        </div>
      )}
    </div>
  );
};

const TableRow = ({
  teamData,
  criterias,
  i,
  splitTablePlaces,
  splitConfig,
  disableRank,
  teamsId,
}: {
  teamData: TeamStanding;
  i: number;
  criterias: string[];
  splitTablePlaces: boolean;
  splitConfig: Record<string, string>;
  disableRank: boolean;
  teamsId: string[];
}) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslation();
  const teamBg = teamsId.includes(teamData?.team?.id) ? 'ui-02' : undefined;
  const isTeamBorder = teamsId.includes(teamData?.team?.id) && isDesktop;
  const qualified = !!teamData?.qualified || !!teamData?.qualificationLabelId;
  const splitPositions = Object.keys(splitConfig ?? {}).map(Number);

  const SplitDivider = useCallback(
    ({ index }: { index: number }) => {
      const [title, subTitle, dotted] = splitConfig[`${index}`]?.split('|') ?? [];
      if (!title) return null;

      return (
        <div className={clsx(style.splitCaption, !!dotted && style.splitCaptionDotted)}>
          {t(title)}
          {subTitle && ` (${t(subTitle)})`}
        </div>
      );
    },
    [splitConfig, t],
  );

  return (
    <>
      {splitTablePlaces && splitPositions.includes(i) && <SplitDivider index={i} />}
      <PkTableRow
        bgColor={teamBg}
        className={
          (clsx(style.row, splitPositions.includes(i + 1) && splitTablePlaces && style.noDivider),
          isTeamBorder && 'pk-rounded--l')
        }
      >
        <div
          className={clsx(
            'pk-d--flex pk-flex--column pk-gap--xs3 pk-my--xs',
            style.bar,
            teamData.qualificationLabelId && style[legendMap[teamData.qualificationLabelId]],
            !qualified && 'pk-visibility--invisible',
            disableRank && 'pk-mr--xs2',
          )}
        />
        {!disableRank && (
          <PkTableCell column-key="rank" className="pk-pr--0">
            {teamData.rank}
          </PkTableCell>
        )}
        <PkTableCell column-key="competitions" className="pk-pl--0">
          <PkIdentifier className="pk-py--xs2 pk-py-md--auto">
            <span slot="prefix" className="pk-mr--s">
              <PkBadge
                alt="Team"
                fallbackImage="club-generic-badge"
                src={teamData?.team?.mediumLogoUrl}
                class="hydrated"
                size={32}
                className="pk-ml--m"
              />
            </span>
            <span slot="primary">{translateEntity(teamData?.team)?.displayName ?? t('team')}</span>
          </PkIdentifier>
        </PkTableCell>

        {Object.entries(teamData)
          .filter((el) => criterias.includes(el[0]))
          .sort((a, b) => criterias.indexOf(a[0]) - criterias.indexOf(b[0]))
          .map((teamStandingData, criteria, arr) => {
            const isLastParameter = arr.length - 1 === criteria;

            return (
              <PkTableCell key={criteria} columnKey={teamStandingData[0]} aligned="center">
                <span
                  className={`pk-text--text-01 ${isDesktop ? 'pk-font-size--m' : 'pk-font-size--s'} ${
                    isLastParameter ? 'pk-font-weight--bolder' : ''
                  }`}
                >
                  {String(teamStandingData[1])}
                </span>
              </PkTableCell>
            );
          })}
      </PkTableRow>
    </>
  );
};
